import { useMemo, useCallback } from 'react';
import type { DailyUsageData } from '../../utils/openai-api';
import { DateRange } from '../../constants/ui';
import type { TimezoneMode } from '../../utils/storage';

import { isDailyAggregationRange } from './constants';
import { getDayStartTimestamp, generateTimeTicks, getHoursInTZ, getMinutesInTZ, getMonthInTZ, getDateInTZ } from './utils';
import type { RequestDataPoint, CostDataPoint, ApiKeyDataPoint } from './types';

interface UseXAxisConfigInput {
  chartSeries: DailyUsageData[];
  requestsData: RequestDataPoint[];
  costData: CostDataPoint[];
  apiKeyData: ApiKeyDataPoint[];
  dateRange: DateRange;
  timezoneMode: TimezoneMode;
}

interface UseXAxisConfigOutput {
  alignedXAxisTicks: number[];
  alignedXAxisInterval: number | 'preserveEnd';
  apiKeyXAxisTicks: number[];
  apiKeyXAxisInterval: number | 'preserveEnd';
  formatTimestamp: (value: number | string) => string;
}

export function useXAxisConfig({
  chartSeries,
  requestsData,
  costData,
  apiKeyData,
  dateRange,
  timezoneMode
}: UseXAxisConfigInput): UseXAxisConfigOutput {
  const xAxisTicks = useMemo(() => {
    const timestamps = requestsData
      .map(point => point.timestamp)
      .filter((value): value is number => Number.isFinite(value));

    return generateTimeTicks(timestamps, dateRange);
  }, [requestsData, dateRange]);

  const computedXAxisInterval = xAxisTicks.length > 0 ? 0 : 'preserveEnd';
  const isDailyRange = isDailyAggregationRange(dateRange);

  const sharedDailyTicks = useMemo(() => {
    if (!isDailyRange) return [];
    const unique = new Set<number>();
    requestsData.forEach(point => {
      if (Number.isFinite(point.timestamp)) {
        unique.add(getDayStartTimestamp(point.timestamp));
      }
    });
    if (unique.size === 0) {
      costData.forEach(point => {
        if (Number.isFinite(point.timestamp)) {
          unique.add(getDayStartTimestamp(point.timestamp));
        }
      });
    }
    if (unique.size === 0) {
      apiKeyData.forEach(point => {
        if (Number.isFinite(point.timestamp)) {
          unique.add(getDayStartTimestamp(point.timestamp));
        }
      });
    }
    return Array.from(unique).sort((a, b) => a - b);
  }, [isDailyRange, requestsData, costData, apiKeyData]);

  const alignedXAxisTicks = isDailyRange && sharedDailyTicks.length > 0 ? sharedDailyTicks : xAxisTicks;
  const alignedXAxisInterval: number | 'preserveEnd' = isDailyRange && sharedDailyTicks.length > 0 ? 0 : computedXAxisInterval;

  const apiKeyXAxisTicks = useMemo(() => {
    if (!isDailyRange) {
      return alignedXAxisTicks;
    }
    return sharedDailyTicks;
  }, [isDailyRange, alignedXAxisTicks, sharedDailyTicks]);

  const apiKeyXAxisInterval: number | 'preserveEnd' = isDailyRange ? 0 : alignedXAxisInterval;

  // タイムスタンプフォーマット関数（TZ-aware）
  const formatTimestamp = useCallback((value: number | string): string => {
    // 文字列の日付の場合（Model Usage Distribution用）
    if (typeof value === 'string') {
      // "2024-01-20 12:00" または "2024-01-20 12:00:00" 形式を処理
      const parts = value.split(' ');
      if (parts.length === 2) {
        const [datePart, timePart] = parts;
        // Parse as UTC and re-format in the selected TZ
        const [year, mon, d] = datePart.split('-').map(Number);
        const [h, m] = timePart.split(':').map(Number);
        const ts = Date.UTC(year, mon - 1, d, h, m, 0);
        const hh = getHoursInTZ(ts, timezoneMode);
        const mm = getMinutesInTZ(ts, timezoneMode);
        return `${hh.toString().padStart(2, '0')}:${mm.toString().padStart(2, '0')}`;
      }
      // 日付のみの場合
      const [year, month, day] = value.split('-').map(Number);
      if (month && day) {
        const ts = Date.UTC(year, month - 1, day);
        return `${getMonthInTZ(ts, timezoneMode)}/${getDateInTZ(ts, timezoneMode)}`;
      }
      return value;
    }
    // 数値のタイムスタンプの場合（他のチャート用）
    if (!Number.isFinite(value)) return '';

    const hours = getHoursInTZ(value, timezoneMode);
    const minutes = getMinutesInTZ(value, timezoneMode);
    const month = getMonthInTZ(value, timezoneMode);
    const day = getDateInTZ(value, timezoneMode);
    const hourMinuteLabel = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

    if (dateRange === '1hour' || dateRange === '3hours' || dateRange === '6hours' || dateRange === '1day' || dateRange === 'yesterday') {
      return hourMinuteLabel;
    }

    if (dateRange === '7days' || dateRange === '30days' || dateRange === '60days' || dateRange === '90days' || dateRange === '120days' || dateRange === '150days' || dateRange === '180days' || dateRange === '360days') {
      return `${month}/${day}`;
    }

    // 時間データの場合（データ範囲が24時間以内）
    const dataRange = chartSeries && chartSeries.length > 0 && chartSeries[0].date.includes(':');
    if (dataRange) {
      return hourMinuteLabel;
    }

    // 日付データの場合 M/D形式
    return `${month}/${day}`;
  }, [dateRange, chartSeries, timezoneMode]);

  return {
    alignedXAxisTicks,
    alignedXAxisInterval,
    apiKeyXAxisTicks,
    apiKeyXAxisInterval,
    formatTimestamp
  };
}
